/**
 * Painel financeiro: inadimplência, ticket médio e faturamento.
 *
 * Definições em vigor, todas conferidas contra o dado real:
 *  · inadimplência = boleto vencido, sem pagamento, e cuja situação a própria
 *    Hinova marca como contável — cancelado e excluído ficam fora;
 *  · ticket médio = mensalidade média POR PLACA, porque boleto de frota cobre
 *    várias e o valor cheio multiplicaria o número;
 *  · faturamento = emitido e recebido lado a lado; a diferença entre as duas
 *    linhas é a inadimplência do mês.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, CircleCheck, Receipt, Users, Wallet } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { moeda, moedaCompacta, numero } from '../../lib/format.ts';
import { Card, Esqueleto, TituloSecao } from '../../components/ui/index.tsx';
import { CartaoDado, GradeBento } from '../../components/ui/Bento.tsx';
import { BarrasPorPeriodo, corSerie, RankingBarras } from '../../components/charts/index.tsx';
import BarraFiltros, {
  filtroInicial,
  type OpcoesFiltro,
  type ValorFiltro,
} from '../../components/filtros/BarraFiltros.tsx';
import { paramsDe } from '../Vendas.tsx';

interface Grupo {
  rotulo: string;
  valor: number;
  placas: number;
}

interface ResumoFinanceiro {
  periodo: { de: string; ate: string; meses: number };
  inadimplencia: {
    titulos: number;
    placas: number;
    valor: number;
    vencido: { titulos: number; placas: number; valor: number };
    totalPeriodo: number;
    pagos: { titulos: number; valor: number };
    porCooperativa: Grupo[];
    porConsultor: Grupo[];
  };
  ticketMedio: {
    geral: number;
    placas: number;
    receitaMensalMedia: number;
    porCooperativa: Grupo[];
  };
  faturamento: { rotulo: string; emitido: number; recebido: number }[];
}

export default function Financeiro() {
  const [filtro, setFiltro] = useState<ValorFiltro>(() => {
    // Doze meses por padrão: faturamento e inadimplência só fazem sentido com
    // série longa. Um mês sozinho não mostra tendência nenhuma.
    const f = filtroInicial();
    const hoje = new Date();
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
    return { ...f, de: inicio.toLocaleDateString('en-CA'), atalho: '12m' };
  });
  const qs = paramsDe(filtro);

  const { data: opcoes } = useQuery({
    queryKey: ['vendas', 'filtros'],
    queryFn: () => api.get<OpcoesFiltro>('/vendas/filtros'),
    staleTime: 10 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['financeiro', qs],
    queryFn: () => api.get<ResumoFinanceiro>(`/financeiro/resumo?${qs}`),
    refetchInterval: 60_000,
  });

  return (
    <div className="flex flex-col gap-5">
      <BarraFiltros valor={filtro} onChange={setFiltro} opcoes={opcoes} />

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Esqueleto key={i} className="h-[100px]" />
            ))}
          </div>
          <Esqueleto className="h-[300px]" />
        </div>
      ) : data ? (
        <>
          <GradeBento>
            <CartaoDado
              rotulo="Em aberto"
              valor={moedaCompacta(data.inadimplencia.valor)}
              nota={`${numero(data.inadimplencia.titulos)} títulos não pagos`}
              Icone={AlertTriangle}
              tom={data.inadimplencia.valor > 0 ? 'perigo' : undefined}
            />
            <CartaoDado
              rotulo="Já vencido"
              valor={moedaCompacta(data.inadimplencia.vencido.valor)}
              nota={`${numero(data.inadimplencia.vencido.titulos)} títulos · o que se cobra hoje`}
              Icone={Receipt}
              tom={data.inadimplencia.vencido.valor > 0 ? 'perigo' : undefined}
            />
            <CartaoDado
              rotulo="Carteira ativa"
              valor={numero(data.ticketMedio.placas)}
              nota="placas sem cancelamento"
              Icone={Wallet}
            />
            <CartaoDado
              rotulo="Recebido no período"
              Icone={CircleCheck}
              tom="sucesso"
              valor={moedaCompacta(data.inadimplencia.pagos.valor)}
              nota={`${numero(data.inadimplencia.pagos.titulos)} títulos baixados`}
            />
          </GradeBento>

          {/* Linha de conferência: os mesmos totais que a tela de boletos do
              SGA mostra para o período, para bater número a número. */}
          <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-[13px]">
            <span className="text-[var(--muted)]">
              No período:{' '}
              <strong className="num text-[var(--fg)]">
                {numero(data.inadimplencia.totalPeriodo)}
              </strong>{' '}
              boletos
            </span>
            <span className="text-[var(--muted)]">
              Em aberto:{' '}
              <strong className="num text-[var(--fg)]">
                {numero(data.inadimplencia.titulos)}
              </strong>{' '}
              ({moeda(data.inadimplencia.valor)})
            </span>
            <span className="text-[var(--muted)]">
              Baixados:{' '}
              <strong className="num text-[var(--fg)]">
                {numero(data.inadimplencia.pagos.titulos)}
              </strong>{' '}
              ({moeda(data.inadimplencia.pagos.valor)})
            </span>
          </Card>

          <Card className="p-5">
            <TituloSecao>Faturamento mensal · mensalidades emitidas × recebidas</TituloSecao>
            <BarrasPorPeriodo
              linhas={data.faturamento.map((f) => ({
                rotulo: mesLongo(f.rotulo),
                valores: { emitido: f.emitido, recebido: f.recebido },
              }))}
              series={[
                { chave: 'emitido', rotulo: 'Emitido', cor: corSerie(1) },
                { chave: 'recebido', rotulo: 'Recebido', cor: 'var(--success)' },
              ]}
              formatar={moeda}
            />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <TituloSecao>
                <span className="inline-flex items-center gap-2">
                  <Building2 size={14} /> Inadimplência por cooperativa
                </span>
              </TituloSecao>
              <RankingBarras
                itens={data.inadimplencia.porCooperativa}
                formatar={moeda}
                rotuloColuna="Cooperativa"
                rotuloMedida="Em aberto"
              />
            </Card>

            <Card className="p-5">
              <TituloSecao>
                <span className="inline-flex items-center gap-2">
                  <Users size={14} /> Inadimplência por consultor
                </span>
              </TituloSecao>
              <RankingBarras
                itens={data.inadimplencia.porConsultor.slice(0, 10)}
                formatar={moeda}
                rotuloColuna="Consultor"
                rotuloMedida="Em aberto"
              />
            </Card>
          </div>

        </>
      ) : null}
    </div>
  );
}

function mesLongo(iso: string): string {
  const [ano, mes] = iso.split('-');
  const d = new Date(Number(ano), Number(mes) - 1, 1);
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}
